import React from "react";
import { Button } from "reactstrap";
import { FaList, FaMap, FaTimes } from "react-icons/fa";
import ListingItem from "../ListingItem";
import Preview from "../Preview";
import Overlay from "../Overlay";
import MapContent from "./MapContent";
import { useListingsMap } from "./hooks/useListingsMap";

/**
 * A component that displays a map and a list of listings.
 *
 * @param {Object[]} listings - An array of objects with the following properties:
 *  - listingId (string): The id of the listing
 *  - latitude (number): The latitude of the listing
 *  - longitude (number): The longitude of the listing
 *  - image (string): The URL of the image for the listing
 *  - price (number): The price of the listing
 *  - bedrooms (number): The number of bedrooms in the listing
 *  - bathrooms (number): The number of bathrooms in the listing
 * @returns {React.ReactElement} - The component
 */
const ListingsMap = ({ listings = [] }) => {
    const {
        setView,
        setHoveredListing,
        isFullscreen,
        isMapView,
        isListView,
        markers,
        previewListings,
        bottomOffset,
        isTablet,
        isMedium,
        isSmall,
    } = useListingsMap({ listings });

    if (!listings || listings.length === 0) {
        return null;
    }
    return (
        <div className="container-fluid position-relative">
            <div className="position-relative">
                <div className="d-md-none">
                    <Preview listings={previewListings} className="mb-4 mt-5" />
                </div>
                <Overlay show={isFullscreen}>
                    <div
                        className={isFullscreen ? "h-100" : "d-none d-md-block"}
                    >
                        <div className="row h-100">
                            <div
                                className={
                                    isFullscreen
                                        ? `col-12 ${isMapView ? "d-none" : ""}`
                                        : "col-4"
                                }
                                style={{
                                    height: `${
                                        isFullscreen ? "100%" : "600px"
                                    }`,
                                    overflowY: "auto",
                                }}
                            >
                                <div className={isListView ? "row gx-1" : ""}>
                                    {listings.map((listing) => (
                                        <div
                                            key={listing.listingId}
                                            className={`${
                                                isListView
                                                    ? "col-12 col-sm-6"
                                                    : ""
                                            } mb-5 ${isListView ? "mt-3" : ""}`}
                                            onMouseEnter={() =>
                                                setHoveredListing(
                                                    listing.listingId
                                                )
                                            }
                                            onMouseLeave={() =>
                                                setHoveredListing(null)
                                            }
                                        >
                                            <ListingItem
                                                listing={listing}
                                                showNicheItem={true}
                                                showSave={true}
                                                pageActionLocation="agent-listings"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div
                                className={
                                    isFullscreen
                                        ? `col-12 ${
                                              isListView
                                                  ? "d-none"
                                                  : "d-flex flex-column h-100"
                                          }`
                                        : "col-8"
                                }
                            >
                                <div
                                    className="flex-grow-1 position-relative"
                                    style={
                                        isFullscreen ? {} : { height: "600px" }
                                    }
                                >
                                    <MapContent
                                        markers={markers}
                                        listings={listings}
                                        isTablet={isTablet}
                                        hideControls={
                                            isSmall || isMedium ? true : false
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="w-100"
                        style={
                            isFullscreen
                                ? {
                                      zIndex: 15,
                                      position: "absolute",
                                      bottom: isListView ? "0" : bottomOffset,
                                  }
                                : {}
                        }
                    >
                        <div
                            className={`d-flex justify-content-center mb-3 ${
                                isFullscreen ? "" : "d-md-none"
                            }`}
                        >
                            {isFullscreen && (isListView || isMapView) && (
                                <Button
                                    color="primary"
                                    onClick={() => setView()}
                                    className="px-3 py-2 me-5"
                                >
                                    <FaTimes className="me-1" />
                                    Close
                                </Button>
                            )}
                            {(!isFullscreen || isListView) && (
                                <Button
                                    color="primary"
                                    className={`px-3 py-2 ${
                                        !isFullscreen ? "me-3" : ""
                                    }`}
                                    onClick={() => setView("map")}
                                >
                                    <FaMap className="me-2" />
                                    Map
                                </Button>
                            )}
                            {(!isFullscreen || isMapView) && (
                                <Button
                                    color="primary"
                                    className="px-3 py-2"
                                    onClick={() => setView("list")}
                                >
                                    <FaList className="me-2" />
                                    List
                                </Button>
                            )}
                        </div>
                    </div>
                </Overlay>
            </div>
        </div>
    );
};

export default ListingsMap;
